import { ReactNode } from 'react';

import { Box, Card, Flex, Heading, Text } from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';
import { SanityImage } from '~/components/sanity-image';
import { CompanyContent } from '~/features/companies/models/company';
import { getPrereqsToCoursePages } from '~/features/program/controllers/get-prerequisite-course-keys';
import { Program } from '~/features/program/models/program';
import { ProgramContent } from '~/features/program/models/program-content';
import { ProgramMetadata } from '~/features/program/models/program-metadata';
import { ProgramReviewSummary } from '~/features/program/models/program-review-summary';
import { getPartTitleFromNanodegree } from '~/features/program/utils/get-part-title-from-program';
import { isCourseProgram, isNanodegreeProgram } from '~/features/program/utils/is-program-type';

import { ProgramPrerequisitesSubsection } from './program-prerequisites-subsection';
import { ProgramSkillsSubsection } from './program-skills-subsection';
import { ProgramStatsCard } from './program-stats-card';

export type ProgramDetailsSectionProps = StaticProgramDetailsSectionProps & {
  priceCardSlot?: ReactNode;
  projectsSlot?: ReactNode;
};

export function ProgramDetailsSection({
  title,
  summary,
  semanticType,
  difficulty,
  duration,
  reviewCount,
  reviewStarsAverage,
  updatedAt,
  allSkills,
  skillsByCourse,
  prerequisites,
  programKey,
  priceCardSlot = null,
  projectsSlot = null,
  partner,
  additionalRequirements,
}: ProgramDetailsSectionProps) {
  return (
    <Box as='section'>
      <ContentContainer
        display='flex'
        flexDirection='column'
        gap={{
          base: 6,
          md: 14,
        }}
      >
        <Box
          display='flex'
          flexDirection='column'
          gap={{
            base: 4,
            md: 8,
          }}
        >
          <Flex
            justifyContent='space-between'
            flexDirection={{
              base: 'column',
              md: 'row',
            }}
            gap={{
              base: 4,
              md: 8,
            }}
          >
            <Flex flexDirection='column' gap={4}>
              <Heading
                as='h1'
                size={{
                  base: 'heading-5',
                  md: 'heading-3',
                }}
              >
                {title}
              </Heading>
              <Text>{summary}</Text>
            </Flex>
            {partner?.logo && (
              <Flex
                alignItems='center'
                as='aside'
                bg='supportOrange.10'
                border='1px solid'
                borderColor='supportOrange.20'
                borderRadius='12px'
                flexDirection='column'
                padding={2}
                alignSelf={{
                  md: 'flex-end',
                }}
                flex={{
                  md: '0 0 152px',
                }}
              >
                <Text fontSize='xs' fontWeight='600' lineHeight='xs' mb={2} textAlign='center'>
                  Built in collaboration with
                </Text>
                <Card boxShadow='none' padding={4} width='100%'>
                  <SanityImage
                    alt={partner?.name}
                    image={partner?.logo}
                    style={{
                      display: 'block',
                      height: '100%',
                      objectFit: 'contain',
                      margin: '0 auto',
                    }}
                    priority
                  />
                </Card>
              </Flex>
            )}
          </Flex>

          <ProgramStatsCard
            difficulty={difficulty}
            duration={duration}
            programKey={programKey}
            reviewCount={reviewCount}
            reviewStarsAverage={reviewStarsAverage}
            semanticType={semanticType}
            updatedAt={updatedAt}
          />
          {priceCardSlot}
        </Box>

        {projectsSlot}

        <ProgramSkillsSubsection
          programKey={programKey}
          programTitle={title}
          skills={allSkills}
          skillsByCourse={skillsByCourse}
          skillsCountTotal={allSkills.length}
        />
        <ProgramPrerequisitesSubsection
          additionalRequirements={additionalRequirements}
          prerequisites={prerequisites}
          programKey={programKey}
        />
      </ContentContainer>
    </Box>
  );
}

export function transformStaticProgramDetailsSectionProps({
  program,
  programContent,
  programMetadata,
  reviewSummary,
  partner,
}: {
  program: Program;
  programContent: ProgramContent;
  programMetadata: ProgramMetadata;
  reviewSummary: ProgramReviewSummary;
  partner: CompanyContent | null;
}) {
  const isNanodegree = isNanodegreeProgram(program);
  const isCourse = isCourseProgram(program);
  const prerequisiteProgramPages = getPrereqsToCoursePages(programMetadata.allPrerequisites, program.key);

  return {
    programKey: program.key,
    title: program.title,
    summary: program.summary,
    semanticType: program.semanticType,
    difficulty: programMetadata.rootProgram?.difficultyLevel?.name || null,
    duration: !isCourse ? program.displayRawDuration : null,
    reviewCount: reviewSummary.numberOfReviews,
    reviewStarsAverage: reviewSummary.starsAverage,
    updatedAt: program.updatedAt,
    allSkills: programMetadata.allSkills,
    skillsByCourse: isNanodegree
      ? programMetadata.courses
          .filter((course) => course.skills.length > 0)
          .reduce(
            (acc, next) => {
              acc[next.key] = {
                skills: next.skills,
                courseTitle: getPartTitleFromNanodegree(program, next.key) ?? '',
              };
              return acc;
            },
            {} as Record<string, { courseTitle: string; skills: string[] }>
          )
      : null,
    prerequisites: programMetadata.allPrerequisites.map((prereq) => ({
      label: prereq,
      url: prerequisiteProgramPages[prereq] || null,
    })),
    additionalRequirements: programContent?.additionalRequirements || null,
    partner: partner ? { name: partner.name, logo: partner.hifiLogo } : null,
  };
}

export type StaticProgramDetailsSectionProps = ReturnType<typeof transformStaticProgramDetailsSectionProps>;
